import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useCurrentAccount } from '@cube3/state/src/redux/components/Administration/withCurrentUserAccount';
import { useResourceList__ALPHA } from '@cube3/state/src/redux/components/Hooks/useResourceList';
import {
  ExportChannelsPicker,
  useExportChannelsStyles
} from '@cube3/ui/src/exports/ExportChannelsPicker';
import { CM360Profile, CM360Config } from '@cube3/common/model/resource-types';
import { ExportTarget } from '@cube3/common/model/schema/resources/export-target';
import { ModalMenuUI } from '@cube3/ui/src/Modal/ModalMenuUI';
import Button from '@cube3/ui/src/Buttons/Button';
import { useModalActions } from '../../Modals/modalActions';
import { hasResolved } from '@cube3/state/src/redux/ducks/request-status/utils/hasResolved';

const emptyArray = [];

export const CM360AccountsModal = (props) => {
  const { modalContext } = props;
  const { exportTargetTypeId } = modalContext;

  const [selectedAccount, setSelectedAccount] =
    useState<ExportTarget<'cm360'>>(null);
  const [selectedProfile, setSelectedProfile] = useState<CM360Profile>(null);

  const { previousModal, openModal } = useModalActions();
  const [currentAccountId] = useCurrentAccount();

  const params = useMemo(() => {
    return {
      filter: {
        exportTargetTypeId: exportTargetTypeId // NOTE: not implemented
      }
    };
  }, [exportTargetTypeId]);

  const {
    resources: exportTargets,
    status: exportTargetStatus
  }: { resources?: ExportTarget<'cm360'>[]; status?: string } =
    useResourceList__ALPHA({
      resourceId: currentAccountId,
      resourceType: 'account',
      edgeType: 'export-target',
      edgeLabel: 'export-targets',
      params,
      strategy: 'fetch-on-mount'
    });

  /** the profiles of the selected account */
  const profiles =
    (selectedAccount &&
      selectedAccount.attributes.integration_config.profiles) ||
    emptyArray;

  const handleNext = useCallback(() => {
    openModal(
      'social_export_options',
      {
        profile: selectedProfile,
        exportTargetId: selectedAccount?.id,
        exportTargetTypeId: exportTargetTypeId,
        platform: 'CM360'
      },
      false
    );
  }, [openModal, selectedAccount, selectedProfile, exportTargetTypeId]);

  const handleManageIntegration = useCallback(() => {
    // open modal version of Account’s Integrations page
    openModal('connected_accounts_modal', null, false);
  }, [openModal]);

  const handleSelectAccount = useCallback((account) => {
    setSelectedAccount(account);
    setSelectedProfile(
      account.channels && account.channels.length === 1
        ? account.channels[0]
        : undefined
    );
  }, []);

  useEffect(() => {
    if (!selectedAccount && exportTargets?.length > 0) {
      setSelectedAccount(exportTargets[0]);
    }
    if (
      selectedAccount &&
      !exportTargets.find((ac) => ac.id === selectedAccount?.id)
    ) {
      setSelectedAccount(null);
    }
  }, [exportTargets, selectedAccount]);

  useEffect(() => {
    if (profiles.length === 1) {
      setSelectedProfile(profiles[0]);
    }
    if (profiles.length === 0) {
      setSelectedProfile(null);
    }
  }, [profiles]);

  const classes = useExportChannelsStyles();

  return (
    <ModalMenuUI
      title="Export to CM360 • Select Account"
      loading={!hasResolved(exportTargetStatus)}
      onCloseEvent={previousModal}
      classes={{ contents: classes.modalContents }}
      wide={true}
      footerLeftComponent={
        <Button
          text={'Manage integration'}
          onClick={handleManageIntegration}
          colorVariant={'ghost1'}
        />
      }
      footerRightComponent={
        <>
          <Button
            text={'Cancel'}
            colorVariant={'ghost2'}
            onClick={previousModal}
            disabled={false}
          />
          <Button
            disabled={!selectedProfile}
            colorVariant={'filled1'}
            text={'Next'}
            onClick={handleNext}
          />
        </>
      }
    >
      {hasResolved(exportTargetStatus) && (
        <ExportChannelsPicker<
          ExportTarget<'cm360'>,
          CM360Config['profiles'][number]
        >
          title="CM360 Account"
          noChannelsMessage="This CM360 account does not have any channels."
          accounts={exportTargets}
          channels={profiles}
          selectedAccount={selectedAccount}
          selectedChannel={selectedProfile}
          onManageIntegration={handleManageIntegration}
          onSelectAccount={handleSelectAccount}
          onSelectChannel={setSelectedProfile}
        />
      )}
    </ModalMenuUI>
  );
};
